/* ============================================================
   EDITOR ARTIKEL LESSON — admin susun kandungan tanpa deploy
   ============================================================
   Dipapar dalam halaman lesson bila admin menekan "Edit artikel".
   Menyunting SALINAN (draf) sahaja; halaman lesson yang menyimpan.

   ⭐ ADMIN SAHAJA → teks hardcoded Bahasa Melayu, TIADA i18n.
      Corak sama ScriptVersionsView dan AnnounceAdminView. Kunci kamus
      untuk alat yang hanya dibuka oleh seorang admin ialah kerja
      penyelenggaraan tanpa pembaca.

   ⭐ PRATONTON memakai komponen PEMAPAR yang SAMA (TutorialArtikel),
      bukan salinan markup. Apa yang admin lihat dalam pratonton ialah
      betul-betul apa yang pelanggan dapat.

   ⛔ GAMBAR DIMAMPAT DI BROWSER SEBELUM NAIK (lebar maks 1400px, JPEG
      0.85) — screenshot 2–5MB menjadi ~150–400KB. Tanpa mampatan ini
      setiap gambar tutorial membakar storage dan egress Firebase pada
      setiap kali halaman dibuka oleh pelanggan.

   ⛔ FAIL YATIM: setiap muat naik didaftarkan melalui `jejakFail` supaya
      halaman lesson boleh memadam fail yang akhirnya tidak dipakai
      (gambar ditukar, blok dibuang, atau admin tekan Batal). Tanpa ini
      storage terkumpul fail yang tiada siapa rujuk.
   ============================================================ */

import { useRef, useState } from 'react';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import {
    ArrowDown, ArrowUp, Eye, Pencil, Plus, Trash2, Upload,
} from 'lucide-react';
import { JENIS_BLOK, newBlok } from '../data/tutorial';
import TutorialArtikel from './TutorialArtikel';
import { GAYA_ARTIKEL } from './artikelKongsi';

const LEBAR_GAMBAR_MAKS = 1400;
const MUTU_JPEG = 0.85;

/* Mampat di browser: lukis semula pada canvas bersaiz terhad, keluarkan
   JPEG. `createImageBitmap` gagal pada fail rosak — pemanggil menangkapnya
   dan memaparkan sebabnya, bukan gagal senyap. */
async function mampatGambar(file) {
    const bitmap = await createImageBitmap(file);
    const skala = Math.min(1, LEBAR_GAMBAR_MAKS / bitmap.width);
    const w = Math.round(bitmap.width * skala);
    const h = Math.round(bitmap.height * skala);
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    canvas.getContext('2d').drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();
    const blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', MUTU_JPEG));
    if (!blob) throw new Error('Gagal memampat gambar.');
    return blob;
}

const GAYA = `
.ed-blok { border:1px solid var(--s-line); border-radius:var(--s-r-lg); background:var(--s-card);
           padding:14px 16px; margin-bottom:12px; }
.ed-blok__bar { display:flex; align-items:center; gap:8px; margin-bottom:12px;
                padding-bottom:10px; border-bottom:1px solid var(--s-line); }
.ed-blok__nama { font-size:11px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; color:var(--s-ink-3); }
.ed-in { width:100%; box-sizing:border-box; padding:9px 11px; border:1px solid var(--s-line-strong);
         border-radius:var(--s-r-sm); background:var(--s-fill); color:var(--s-ink);
         font:inherit; font-size:14px; line-height:1.6; }
.ed-in:focus { outline:none; border-color:var(--s-ink-3); }
.ed-in--ta { resize:vertical; min-height:78px; }
.ed-lbl { display:block; font-size:11px; font-weight:700; letter-spacing:.07em; text-transform:uppercase;
          color:var(--s-ink-3); margin:0 0 5px; }
.ed-baris { display:flex; align-items:flex-start; gap:7px; margin-bottom:8px; }
.ed-baris > .ed-in { flex:1; }
.ed-mini { display:inline-flex; align-items:center; justify-content:center; gap:5px; flex:none;
           height:30px; min-width:30px; padding:0 8px; border:1px solid var(--s-line-strong);
           border-radius:var(--s-r-sm); background:var(--s-card); color:var(--s-ink-2);
           font:inherit; font-size:12.5px; cursor:pointer; transition:background .12s ease, color .12s ease; }
.ed-mini:hover { background:var(--s-fill-hover); color:var(--s-ink); }
.ed-mini:disabled { opacity:.4; cursor:default; }
.ed-mini--stop:hover { color:var(--s-stop); border-color:var(--s-stop); }
.ed-tambah { display:flex; flex-wrap:wrap; gap:7px; padding:16px; margin-top:6px;
             border:1px dashed var(--s-line-strong); border-radius:var(--s-r-lg); background:var(--s-fill); }
.ed-tambah__t { width:100%; font-size:11px; font-weight:700; letter-spacing:.08em; text-transform:uppercase;
                color:var(--s-ink-3); margin-bottom:2px; }
.ed-jenis { display:flex; flex-direction:column; align-items:flex-start; gap:1px;
            padding:8px 11px; border:1px solid var(--s-line-strong); border-radius:var(--s-r-sm);
            background:var(--s-card); color:var(--s-ink); font:inherit; font-size:13.5px; font-weight:500;
            text-align:left; cursor:pointer; transition:background .12s ease; }
.ed-jenis:hover { background:var(--s-fill-hover); }
.ed-jenis small { font-size:11px; font-weight:400; color:var(--s-ink-3); }
.ed-gbr { display:flex; gap:12px; align-items:flex-start; margin-bottom:10px; }
.ed-gbr img { width:200px; height:auto; border-radius:var(--s-r-sm); border:1px solid var(--s-line); display:block; }
.ed-gbr__kosong { width:200px; aspect-ratio:16/9; flex:none; border:1px dashed var(--s-line-strong);
                  border-radius:var(--s-r-sm); background:var(--s-fill); display:grid; place-items:center;
                  font-size:12px; color:var(--s-ink-3); }
.ed-jad { display:grid; grid-template-columns:1fr 1fr auto; gap:7px; margin-bottom:8px; align-items:start; }
`;

function MiniBtn({ icon, children, tone, ...rest }) {
    return (
        <button type="button" className={`ed-mini${tone === 'stop' ? ' ed-mini--stop' : ''}`} {...rest}>
            {icon}{children}
        </button>
    );
}

/* Senarai teks berulang (langkah, senarai bertanda). Satu takrifan
   untuk dua jenis blok — kalau disalin, satu daripadanya pasti
   ketinggalan bila kelakuan diubah. */
function SenaraiTeks({ items, label, satuan, onUbah, banyakBaris }) {
    const tulis = (i, v) => onUbah(arr => { arr[i] = v; });
    const alih = (i, ke) => onUbah(arr => {
        if (ke < 0 || ke >= arr.length) return;
        const [x] = arr.splice(i, 1); arr.splice(ke, 0, x);
    });
    return (
        <>
            <span className="ed-lbl">{label}</span>
            {items.map((it, i) => (
                <div className="ed-baris" key={i}>
                    {banyakBaris
                        ? <textarea className="ed-in ed-in--ta" style={{ minHeight: 56 }} value={it} onChange={(e) => tulis(i, e.target.value)} />
                        : <input className="ed-in" value={it} onChange={(e) => tulis(i, e.target.value)} />}
                    <MiniBtn icon={<ArrowUp size={13} strokeWidth={2} />} title="Naik" disabled={i === 0} onClick={() => alih(i, i - 1)} />
                    <MiniBtn icon={<ArrowDown size={13} strokeWidth={2} />} title="Turun" disabled={i === items.length - 1} onClick={() => alih(i, i + 1)} />
                    <MiniBtn icon={<Trash2 size={13} strokeWidth={2} />} tone="stop" title="Buang" onClick={() => onUbah(arr => { arr.splice(i, 1); })} />
                </div>
            ))}
            <MiniBtn icon={<Plus size={13} strokeWidth={2} />} onClick={() => onUbah(arr => { arr.push(''); })}>{satuan}</MiniBtn>
        </>
    );
}

/* Medan sunting bagi satu blok. `ubah(fn)` menerima objek blok itu
   sahaja — halaman lesson yang menguruskan klon draf. */
function MedanBlok({ blok, ubah, lessonId, jejakFail }) {
    const fileRef = useRef(null);
    const [naik, setNaik] = useState(false);
    const [ralat, setRalat] = useState('');

    const pilihFail = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';                       // benarkan pilih fail SAMA sekali lagi
        if (!file) return;
        if (!file.type.startsWith('image/')) { setRalat('Fail itu bukan gambar.'); return; }
        setRalat(''); setNaik(true);
        try {
            const blob = await mampatGambar(file);
            const path = `tutorial/${lessonId}_${blok.id}_${Date.now()}.jpg`;
            await uploadBytes(storageRef(storage, path), blob, {
                contentType: 'image/jpeg',
                cacheControl: 'public, max-age=31536000, immutable',   // path ada cap masa
            });
            const url = await getDownloadURL(storageRef(storage, path));
            jejakFail(path);
            ubah(b => { b.url = url; b.path = path; });
        } catch (err) {
            console.error('[Tutorial] muat naik gambar gagal:', err);
            setRalat('Muat naik gagal. Cuba lagi.');
        } finally {
            setNaik(false);
        }
    };

    switch (blok.jenis) {
        case 'tajuk':
            return (
                <>
                    <span className="ed-lbl">Tajuk seksyen</span>
                    <input className="ed-in" value={blok.teks} placeholder="Cth: 3. Simpan link Shopee dalam My Products"
                        onChange={(e) => ubah(b => { b.teks = e.target.value; })} />
                </>
            );

        case 'teks':
            return (
                <>
                    <span className="ed-lbl">Perenggan</span>
                    <textarea className="ed-in ed-in--ta" value={blok.teks} placeholder="Tulis di sini. Link yang ditaip akan jadi pautan hidup sendiri."
                        onChange={(e) => ubah(b => { b.teks = e.target.value; })} />
                </>
            );

        case 'langkah':
            return (
                <SenaraiTeks
                    items={blok.items} label="Langkah (satu baris satu langkah)" satuan="Tambah langkah" banyakBaris
                    onUbah={(fn) => ubah(b => { fn(b.items); })}
                />
            );

        case 'semak':
            return (
                <>
                    <span className="ed-lbl">Tajuk kotak (kosongkan = senarai biasa tanpa kotak)</span>
                    <input className="ed-in" value={blok.tajuk} placeholder="Cth: SELEPAS HABIS ARTIKEL INI, ANDA BOLEH"
                        onChange={(e) => ubah(b => { b.tajuk = e.target.value; })} style={{ marginBottom: 12 }} />
                    <SenaraiTeks
                        items={blok.items} label="Perkara" satuan="Tambah perkara"
                        onUbah={(fn) => ubah(b => { fn(b.items); })}
                    />
                </>
            );

        case 'gambar':
            return (
                <>
                    <div className="ed-gbr">
                        {blok.url
                            ? <img src={blok.url} alt="" />
                            : <div className="ed-gbr__kosong">Belum ada gambar</div>}
                        <div style={{ display: 'grid', gap: 7, alignContent: 'start' }}>
                            <input ref={fileRef} type="file" accept="image/*" onChange={pilihFail} style={{ display: 'none' }} />
                            <MiniBtn icon={<Upload size={13} strokeWidth={2} />} disabled={naik} onClick={() => fileRef.current?.click()}>
                                {naik ? 'Sedang naik…' : blok.url ? 'Tukar gambar' : 'Muat naik gambar'}
                            </MiniBtn>
                            {blok.url && (
                                <MiniBtn icon={<Trash2 size={13} strokeWidth={2} />} tone="stop"
                                    onClick={() => ubah(b => { b.url = ''; b.path = ''; })}>Buang gambar</MiniBtn>
                            )}
                            {ralat && <span style={{ fontSize: 12, color: 'var(--s-stop)' }}>{ralat}</span>}
                            <span style={{ fontSize: 11.5, color: 'var(--s-ink-3)', maxWidth: 240, lineHeight: 1.5 }}>
                                Gambar dimampat sendiri sebelum naik. Screenshot penuh skrin memadai.
                            </span>
                        </div>
                    </div>
                    <span className="ed-lbl">Kapsyen (pilihan)</span>
                    <input className="ed-in" value={blok.kapsyen} placeholder="Satu baris penerangan di bawah gambar"
                        onChange={(e) => ubah(b => { b.kapsyen = e.target.value; })} />
                </>
            );

        case 'nota':
            return (
                <>
                    <span className="ed-lbl">Jenis kotak</span>
                    <select className="ed-in" value={blok.gaya} onChange={(e) => ubah(b => { b.gaya = e.target.value; })}
                        style={{ marginBottom: 12 }}>
                        <option value="tip">Tip — cadangan berguna</option>
                        <option value="info">Maklumat — perkara yang normal</option>
                        <option value="amaran">Hati-hati — mudah tersilap</option>
                        <option value="stop">Jangan buat — akan rosak</option>
                    </select>
                    <span className="ed-lbl">Tajuk kotak (pilihan)</span>
                    <input className="ed-in" value={blok.tajuk} placeholder="Cth: Jangan guna Business Suite"
                        onChange={(e) => ubah(b => { b.tajuk = e.target.value; })} style={{ marginBottom: 12 }} />
                    <span className="ed-lbl">Isi kotak</span>
                    <textarea className="ed-in ed-in--ta" value={blok.teks}
                        onChange={(e) => ubah(b => { b.teks = e.target.value; })} />
                </>
            );

        case 'jadual':
            return (
                <>
                    <span className="ed-lbl">Tajuk lajur</span>
                    <div className="ed-jad" style={{ gridTemplateColumns: '1fr 1fr' }}>
                        <input className="ed-in" value={blok.kepala[0]} placeholder="Lajur kiri"
                            onChange={(e) => ubah(b => { b.kepala[0] = e.target.value; })} />
                        <input className="ed-in" value={blok.kepala[1]} placeholder="Lajur kanan"
                            onChange={(e) => ubah(b => { b.kepala[1] = e.target.value; })} />
                    </div>
                    <span className="ed-lbl" style={{ marginTop: 12 }}>Baris</span>
                    {blok.baris.map((r, i) => (
                        <div className="ed-jad" key={i}>
                            <input className="ed-in" value={r[0]} onChange={(e) => ubah(b => { b.baris[i][0] = e.target.value; })} />
                            <input className="ed-in" value={r[1]} onChange={(e) => ubah(b => { b.baris[i][1] = e.target.value; })} />
                            <MiniBtn icon={<Trash2 size={13} strokeWidth={2} />} tone="stop" title="Buang baris"
                                onClick={() => ubah(b => { b.baris.splice(i, 1); })} />
                        </div>
                    ))}
                    <MiniBtn icon={<Plus size={13} strokeWidth={2} />}
                        onClick={() => ubah(b => { b.baris.push(['', '']); })}>Tambah baris</MiniBtn>
                </>
            );

        case 'sj':
            return (
                <>
                    <span className="ed-lbl">Soalan dan jawapan</span>
                    {blok.items.map((x, i) => (
                        <div key={i} style={{ borderLeft: '2px solid var(--s-line-strong)', paddingLeft: 12, marginBottom: 12 }}>
                            <div className="ed-baris">
                                <input className="ed-in" value={x.s} placeholder="Soalan / mesej ralat yang pelanggan nampak"
                                    onChange={(e) => ubah(b => { b.items[i].s = e.target.value; })} />
                                <MiniBtn icon={<Trash2 size={13} strokeWidth={2} />} tone="stop" title="Buang"
                                    onClick={() => ubah(b => { b.items.splice(i, 1); })} />
                            </div>
                            <textarea className="ed-in ed-in--ta" style={{ minHeight: 56 }} value={x.j} placeholder="Jawapan"
                                onChange={(e) => ubah(b => { b.items[i].j = e.target.value; })} />
                        </div>
                    ))}
                    <MiniBtn icon={<Plus size={13} strokeWidth={2} />}
                        onClick={() => ubah(b => { b.items.push({ s: '', j: '' }); })}>Tambah soalan</MiniBtn>
                </>
            );

        default:
            return null;
    }
}

export default function TutorialArtikelEditor({ lesson, mutate, jejakFail }) {
    const [pratonton, setPratonton] = useState(false);
    const blocks = lesson.blocks || [];

    const ubahBlok = (i, fn) => mutate(l => { fn(l.blocks[i]); });
    const alihBlok = (i, ke) => mutate(l => {
        if (ke < 0 || ke >= l.blocks.length) return;
        const [x] = l.blocks.splice(i, 1); l.blocks.splice(ke, 0, x);
    });
    const buangBlok = (i) => {
        if (!window.confirm('Buang blok ini?')) return;
        mutate(l => { l.blocks.splice(i, 1); });
    };
    const tambahBlok = (jenis) => mutate(l => {
        const b = newBlok(jenis);
        if (b) l.blocks.push(b);
    });

    return (
        <div style={{ maxWidth: 760 }}>
            <style>{GAYA}{GAYA_ARTIKEL}</style>

            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <MiniBtn icon={pratonton ? <Pencil size={13} strokeWidth={2} /> : <Eye size={13} strokeWidth={2} />}
                    onClick={() => setPratonton(p => !p)}>
                    {pratonton ? 'Kembali menyunting' : 'Pratonton'}
                </MiniBtn>
                <span style={{ fontSize: 12.5, color: 'var(--s-ink-3)', alignSelf: 'center' }}>
                    {pratonton ? 'Inilah rupa sebenar yang pelanggan nampak.' : `${blocks.length} blok`}
                </span>
            </div>

            {pratonton ? (
                <TutorialArtikel lesson={lesson} />
            ) : (
                <>
                    <div className="ed-blok">
                        <span className="ed-lbl">Tajuk lesson</span>
                        <input className="ed-in" value={lesson.title} placeholder="Cth: Shopee Affiliate ke Facebook Reels"
                            onChange={(e) => mutate(l => { l.title = e.target.value; })} style={{ marginBottom: 12 }} />
                        <span className="ed-lbl">Perenggan pembuka</span>
                        <textarea className="ed-in ed-in--ta" value={lesson.body}
                            placeholder="Satu perenggan ringkas di bawah video — apa artikel ini akan ajar."
                            onChange={(e) => mutate(l => { l.body = e.target.value; })} />
                    </div>

                    {blocks.map((b, i) => {
                        const meta = JENIS_BLOK.find(j => j.jenis === b.jenis);
                        return (
                            <div className="ed-blok" key={b.id}>
                                <div className="ed-blok__bar">
                                    <span className="ed-blok__nama">{i + 1} · {meta?.nama || b.jenis}</span>
                                    <span style={{ flex: 1 }} />
                                    <MiniBtn icon={<ArrowUp size={13} strokeWidth={2} />} title="Alih ke atas"
                                        disabled={i === 0} onClick={() => alihBlok(i, i - 1)} />
                                    <MiniBtn icon={<ArrowDown size={13} strokeWidth={2} />} title="Alih ke bawah"
                                        disabled={i === blocks.length - 1} onClick={() => alihBlok(i, i + 1)} />
                                    <MiniBtn icon={<Trash2 size={13} strokeWidth={2} />} tone="stop" title="Buang blok"
                                        onClick={() => buangBlok(i)} />
                                </div>
                                <MedanBlok blok={b} lessonId={lesson.id} jejakFail={jejakFail}
                                    ubah={(fn) => ubahBlok(i, fn)} />
                            </div>
                        );
                    })}

                    <div className="ed-tambah">
                        <span className="ed-tambah__t">Tambah blok</span>
                        {JENIS_BLOK.map(j => (
                            <button key={j.jenis} type="button" className="ed-jenis" onClick={() => tambahBlok(j.jenis)}>
                                {j.nama}
                                <small>{j.nota}</small>
                            </button>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
